import { ReactNode, useCallback, useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import DialogTitle from '@rossum/components/dialog-title/DialogTitle';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Link,
  Stack,
  Theme,
  Typography,
} from '@rossum/ui/material';
import { isEmbedded } from '../../../../../constants/config';
import {
  BUILTIN_CHECKS_LINK,
  CUSTOM_CHECKS_LINK,
  HISTORY_CHECKS_LINK,
} from '../../../../../constants/values';
import { annotationSelector } from '../../../../../redux/modules/annotation/selectors';
import { hideArticleLinksSelector } from '../../../../../redux/modules/organization/selectors';
import { formatConfidenceScore, Item } from '../utils';

type ValidationSourceProps = {
  name: string;
  linkHref?: string;
  description?: string;
  nameSuffix?: ReactNode;
};

const helpLinksMap: Record<string, string> = {
  checks: BUILTIN_CHECKS_LINK,
  history: HISTORY_CHECKS_LINK,
  connector: CUSTOM_CHECKS_LINK,
} as const;

const ValidationSource = ({
  name,
  linkHref,
  description,
  nameSuffix,
}: ValidationSourceProps) => {
  const linkProps = linkHref
    ? {
        component: Link,
        href: linkHref,
        target: '_blank',
        rel: 'noreferrer noopener',
        sx: {
          color: (theme: Theme) => theme.palette.secondary.main,
          '--Link-underlineColor': (theme: Theme) =>
            theme.palette.secondary.main,
          '&:hover': {
            textDecoration: 'none',
          },
        },
      }
    : { component: 'span' };

  return (
    <Stack
      component="li"
      display="list-item"
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{ lineHeight: 'initial' }}
      divider={
        <Typography component="span" variant="body2" color="text.secondary">
          -
        </Typography>
      }
    >
      <Typography
        whiteSpace="nowrap"
        fontWeight="bold"
        variant="body2"
        {...linkProps}
      >
        {name}
      </Typography>
      {nameSuffix}

      {description ? (
        <Typography component="span" variant="body2" color="text.secondary">
          {description}
        </Typography>
      ) : null}
    </Stack>
  );
};

type FieldDetailsDialogProps = {
  onClose: () => void;
  validationSources: string[];
  title: ReactNode;
  confidenceScore: number | null;
};

const FieldDetailsDialog = ({
  onClose,
  validationSources,
  title,
  confidenceScore,
}: FieldDetailsDialogProps) => {
  const intl = useIntl();
  const hideLinks = useSelector(hideArticleLinksSelector);

  const sortedValidationSources = useMemo(
    () => R.sortBy(validationSources, source => source !== 'human'), // put human at the beginning
    [validationSources]
  );

  return (
    <Dialog
      open
      onClose={onClose}
      slotProps={{
        paper: {
          elevation: 0,
          sx: { width: 450 },
        },
      }}
    >
      <DialogTitle onClose={onClose} title={title} />
      <DialogContent>
        <Stack sx={{ py: 2 }} spacing={1}>
          <Stack>
            <Typography variant="h6">
              {intl.formatMessage({
                id: 'components.sidebarV2.fieldDetailsDialog.validatedBy.title',
              })}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {intl.formatMessage({
                id: 'components.sidebarV2.fieldDetailsDialog.validatedBy.description',
              })}
            </Typography>
          </Stack>
          <Stack component="ul" sx={{ pl: 3 }}>
            {sortedValidationSources.map(source => {
              const linkHref =
                isEmbedded() || hideLinks ? undefined : helpLinksMap[source];

              switch (source) {
                case 'connector':
                case 'non_required':
                case 'human':
                case 'additional_value':
                case 'default_value':
                case 'history':
                case 'rules':
                case 'data_matching':
                case 'checks':
                case 'einvoicing':
                case 'table_suggester':
                case 'not_found':
                case 'score':
                  return (
                    <ValidationSource
                      key={source}
                      linkHref={linkHref}
                      name={intl.formatMessage({
                        id: `components.sidebarV2.fieldDetailsDialog.validationSources.${source}.name`,
                      })}
                      nameSuffix={
                        source === 'score' && confidenceScore ? (
                          <Typography
                            variant="body2"
                            component="span"
                            fontWeight="normal"
                          >
                            {intl.formatMessage(
                              {
                                id: `components.sidebarV2.fieldDetailsDialog.validationSources.score.confidence`,
                              },
                              { score: formatConfidenceScore(confidenceScore) }
                            )}
                          </Typography>
                        ) : null
                      }
                      description={intl.formatMessage({
                        id: `components.sidebarV2.fieldDetailsDialog.validationSources.${source}.description`,
                      })}
                    />
                  );

                default:
                  return (
                    <ValidationSource
                      key={source}
                      name={intl.formatMessage(
                        {
                          id: `components.sidebarV2.fieldDetailsDialog.validationSources.other.name`,
                        },
                        { source }
                      )}
                    />
                  );
              }
            })}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button variant="outlined" color="secondary" onClick={onClose}>
          {intl.formatMessage({
            id: 'components.sidebarV2.fieldDetailsDialog.close',
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const getRelevantSources = (
  validationSources: string[],
  automated: boolean
): string[] => {
  return validationSources.filter(
    // 'NA' is used by BE for fields that should not block automation (formulas, reasoning, data, manual fields)
    // 'not_found' is filtered only when document has not been automated
    s => !(s === 'NA' || (s === 'not_found' && !automated))
  );
};

export const useFieldDetailsDialog = (item: Item) => {
  const { automated = false } = useSelector(annotationSelector) ?? {};
  const [fieldDetailsDialogOpen, setFieldDetailsDialogOpen] = useState(false);
  const allValidationSources = useMemo(
    () => ('validationSources' in item ? item.validationSources : []),
    [item]
  );

  const relevantValidationSources = useMemo(
    () => getRelevantSources(allValidationSources, automated),
    [allValidationSources, automated]
  );

  const fieldDetailsAvailable = !!relevantValidationSources.length;

  const handleDialogOpen = useCallback(
    () => setFieldDetailsDialogOpen(true),
    []
  );

  const handleDialogClose = useCallback(() => {
    setFieldDetailsDialogOpen(false);
  }, []);

  const fieldDetailsDialog = fieldDetailsDialogOpen ? (
    <FieldDetailsDialog
      onClose={handleDialogClose}
      validationSources={relevantValidationSources}
      confidenceScore={'confidenceScore' in item ? item.confidenceScore : null}
      title={
        'parentLabel' in item ? (
          <>
            {item.parentLabel}{' '}
            <Box component="span" color="text.secondary">
              ({item.parentSchemaId})
            </Box>
          </>
        ) : (
          <>
            {item.label}{' '}
            <Box component="span" color="text.secondary">
              ({item.schemaId})
            </Box>
          </>
        )
      }
    />
  ) : null;

  return {
    fieldDetailsAvailable,
    fieldDetailsDialog,
    openFieldDetailsDialog: handleDialogOpen,
  };
};
